/**
 * Estate Contact Document Workflow Service
 *
 * Provides country-aware document requirement evaluation for legacy
 * contacts and executors.  Requirements are defined in a static
 * registry (v1 — no DB-managed config matrix yet) and evaluated
 * against the document_category values of linked estate documents.
 *
 * Enforcement policy (v1):
 *   - South Africa (ZA) and United States (US): soft_block for required docs.
 *   - United Kingdom (GB), major EU countries (DE, FR, NL, BE, ES, IT),
 *     and other Americas (CA, AU): required severity, warn enforcement.
 *   - All other countries: recommended severity, warn enforcement.
 *
 * @module services/EstateContactDocumentWorkflowService
 */

import { supabase } from '@xanectech/supabase-client/supabase-client';
import { createLogger, getErrorMessage } from '@xanectech/lib';
import type { Database } from '@xanectech/types';
import type {
  ContactDocRole,
  ContactRole,
  DocRequirementEnforcement,
  DocRequirementSeverity,
  DocWorkflowStatus,
  DocWorkflowStatusSnapshot,
  DocumentRequirementItem,
} from '@xanectech/types';

const logger = createLogger('EstateContactDocumentWorkflowService');

// ─────────────────────────────────────────────────────────────────────────────
// Row type aliases
// ─────────────────────────────────────────────────────────────────────────────

type EstateContact =
  Database['public']['Tables']['estate_vault_contacts']['Row'];

// ─────────────────────────────────────────────────────────────────────────────
// Country classifications
// ─────────────────────────────────────────────────────────────────────────────

/** v1 high-risk countries — missing required docs trigger a soft_block. */
const HIGH_RISK_COUNTRIES = new Set<string>(['ZA', 'US']);

/**
 * Countries where identity proof is treated as required (not just
 * recommended) but enforcement is warn rather than soft_block.
 */
const ELEVATED_WARN_COUNTRIES = new Set<string>([
  'GB',
  'DE',
  'FR',
  'NL',
  'BE',
  'ES',
  'IT',
  'CA',
  'AU',
]);

// ─────────────────────────────────────────────────────────────────────────────
// Base requirement definitions (no country override)
// ─────────────────────────────────────────────────────────────────────────────

interface BaseRequirement {
  key: ContactDocRole;
  label: string;
  description: string;
  roles: ContactRole[];
}

const BASE_REQUIREMENTS: BaseRequirement[] = [
  {
    key: 'identity_proof',
    label: 'Identity Proof',
    description:
      'A valid government-issued ID document (e.g. passport, national ID card) for the contact.',
    roles: ['legacy_contact', 'executor_person'],
  },
  {
    key: 'appointment_letter',
    label: 'Appointment Letter',
    description:
      'A signed document formally appointing the person or firm as executor.',
    roles: ['executor_person', 'executor_firm'],
  },
  {
    key: 'relationship_proof',
    label: 'Relationship Proof',
    description:
      'Documentation evidencing the stated relationship (e.g. marriage certificate, birth certificate).',
    roles: ['legacy_contact'],
  },
  {
    key: 'authority_document',
    label: 'Authority Document',
    description:
      'A legal authority document such as a power of attorney granting estate administration rights.',
    roles: ['executor_person', 'executor_firm'],
  },
  {
    key: 'registration_certificate',
    label: 'Registration Certificate',
    description:
      'Official registration or incorporation certificate for the executor firm.',
    roles: ['executor_firm'],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Country-aware requirement resolution
// ─────────────────────────────────────────────────────────────────────────────

function resolveEnforcement(
  countryCode: string | null,
): { severity: DocRequirementSeverity; enforcement: DocRequirementEnforcement } {
  const code = countryCode?.toUpperCase() ?? null;

  if (code && HIGH_RISK_COUNTRIES.has(code)) {
    return { severity: 'required', enforcement: 'soft_block' };
  }
  if (code && ELEVATED_WARN_COUNTRIES.has(code)) {
    return { severity: 'required', enforcement: 'warn' };
  }
  return { severity: 'recommended', enforcement: 'warn' };
}

/**
 * Build the resolved requirement list for a contact role and country.
 * Returns only requirements that apply to the given role.
 */
export function resolveRequirements(
  role: ContactRole,
  countryCode: string | null,
): DocumentRequirementItem[] {
  const { severity, enforcement } = resolveEnforcement(countryCode);

  return BASE_REQUIREMENTS.filter((r) => r.roles.includes(role)).map((r) => ({
    key: r.key,
    label: r.label,
    description: r.description,
    severity,
    enforcement,
    roles: r.roles,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Evaluation helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute a workflow status snapshot given resolved requirements and
 * the set of document_category values already linked.
 */
export function computeWorkflowSnapshot(
  requirements: DocumentRequirementItem[],
  linkedDocRoles: Set<string>,
  countryCode: string | null,
  existing?: Partial<DocWorkflowStatusSnapshot>,
): DocWorkflowStatusSnapshot {
  const satisfied: ContactDocRole[] = [];
  const missingRequired: ContactDocRole[] = [];
  const missingRecommended: ContactDocRole[] = [];

  for (const req of requirements) {
    if (linkedDocRoles.has(req.key)) {
      satisfied.push(req.key);
    } else if (req.severity === 'required') {
      missingRequired.push(req.key);
    } else {
      missingRecommended.push(req.key);
    }
  }

  let status: DocWorkflowStatus;
  if (requirements.length === 0 || countryCode === null) {
    status = 'not_evaluated';
  } else if (missingRequired.length > 0) {
    status = 'missing_required';
  } else if (missingRecommended.length > 0) {
    status = 'missing_recommended';
  } else {
    status = 'satisfied';
  }

  return {
    status,
    missing_required: missingRequired,
    missing_recommended: missingRecommended,
    satisfied,
    country_code: countryCode,
    evaluated_at: new Date().toISOString(),
    soft_block_acknowledged: existing?.soft_block_acknowledged ?? false,
    soft_block_acknowledged_at: existing?.soft_block_acknowledged_at ?? null,
    soft_block_acknowledged_reason:
      existing?.soft_block_acknowledged_reason ?? null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// access_scope helpers
// ─────────────────────────────────────────────────────────────────────────────

function readDocWorkflow(
  row: EstateContact,
): Partial<DocWorkflowStatusSnapshot> {
  const scope = row.access_scope as Record<string, unknown> | null;
  const dw = scope?.doc_workflow as Record<string, unknown> | undefined;
  if (!dw) return {};
  return {
    status: dw.status as DocWorkflowStatus | undefined,
    missing_required: (dw.missing_required as ContactDocRole[]) ?? [],
    missing_recommended: (dw.missing_recommended as ContactDocRole[]) ?? [],
    satisfied: (dw.satisfied as ContactDocRole[]) ?? [],
    country_code: (dw.country_code as string | null) ?? null,
    evaluated_at: (dw.evaluated_at as string) ?? new Date().toISOString(),
    soft_block_acknowledged:
      (dw.soft_block_acknowledged as boolean) ?? false,
    soft_block_acknowledged_at:
      (dw.soft_block_acknowledged_at as string | null) ?? null,
    soft_block_acknowledged_reason:
      (dw.soft_block_acknowledged_reason as string | null) ?? null,
  };
}

function mergeDocWorkflowIntoScope(
  row: EstateContact,
  snapshot: DocWorkflowStatusSnapshot,
): Record<string, unknown> {
  const existing = (row.access_scope as Record<string, unknown> | null) ?? {};
  return { ...existing, doc_workflow: snapshot };
}

// ─────────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────────

export class EstateContactDocumentWorkflowService {
  /**
   * Evaluate and persist the document workflow status for a contact.
   *
   * Fetches the contact row and all linked estate documents, resolves
   * requirements for the contact's role and country, and stores the
   * resulting snapshot into access_scope.doc_workflow.
   *
   * @param userId  Owner user ID.
   * @param contactId  estate_vault_contacts row ID.
   * @param role  Resolved contact role (legacy_contact | executor_person | executor_firm).
   * @param countryCode  ISO 3166-1 alpha-2 country code or null.
   * @returns The computed snapshot.
   */
  static async evaluateAndPersist(
    userId: string,
    contactId: string,
    role: ContactRole,
    countryCode: string | null,
  ): Promise<DocWorkflowStatusSnapshot> {
    // 1. Fetch existing contact row to read current access_scope
    const { data: contactRow, error: contactErr } = await supabase
      .from('estate_vault_contacts')
      .select('*')
      .eq('id', contactId)
      .eq('user_id', userId)
      .single();

    if (contactErr || !contactRow) {
      logger.error('Failed to fetch contact for workflow evaluation', {
        context: 'EstateContactDocumentWorkflowService',
        error: getErrorMessage(contactErr),
      });
      throw new Error('WORKFLOW_CONTACT_NOT_FOUND');
    }

    // 2. Fetch all linked document categories for this user
    const { data: links, error: linksErr } = await supabase
      .from('estate_vault_document_links')
      .select('document_category')
      .eq('user_id', userId)
      .not('document_category', 'is', null);

    if (linksErr) {
      logger.warn(
        'Failed to fetch estate document links for workflow evaluation',
        {
          context: 'EstateContactDocumentWorkflowService',
          error: getErrorMessage(linksErr),
        },
      );
    }

    const linkedDocRoles = new Set<string>(
      (links ?? [])
        .map((l) => l.document_category)
        .filter((c): c is string => Boolean(c)),
    );

    // 3. Resolve requirements and compute snapshot
    const requirements = resolveRequirements(role, countryCode);
    const existing = readDocWorkflow(contactRow);
    const snapshot = computeWorkflowSnapshot(
      requirements,
      linkedDocRoles,
      countryCode,
      existing,
    );

    // 4. Persist snapshot into access_scope.doc_workflow
    const updatedScope = mergeDocWorkflowIntoScope(contactRow, snapshot);

    const { error: updateErr } = await supabase
      .from('estate_vault_contacts')
      .update({ access_scope: updatedScope })
      .eq('id', contactId)
      .eq('user_id', userId);

    if (updateErr) {
      logger.warn(
        'Failed to persist document workflow snapshot — returning computed value',
        {
          context: 'EstateContactDocumentWorkflowService',
          error: getErrorMessage(updateErr),
        },
      );
    }

    // 5. Emit audit event (non-blocking)
    void EstateContactDocumentWorkflowService.emitAuditEvent(userId, {
      action: 'doc_workflow_evaluated',
      resource_type: 'estate_vault_contact',
      resource_id: contactId,
      details: {
        status: snapshot.status,
        role,
        country_code: countryCode,
        missing_required: snapshot.missing_required,
      },
    });

    return snapshot;
  }

  /**
   * Read the current workflow snapshot from access_scope without
   * triggering a re-evaluation.  Returns null when no snapshot exists.
   */
  static readSnapshot(row: EstateContact): DocWorkflowStatusSnapshot | null {
    const scope = row.access_scope as Record<string, unknown> | null;
    if (!scope?.doc_workflow) return null;
    return readDocWorkflow(row) as DocWorkflowStatusSnapshot;
  }

  /**
   * Acknowledge a soft-block for a contact, storing the reason and
   * timestamp in the snapshot.
   *
   * @param userId     Owner user ID.
   * @param contactId  estate_vault_contacts row ID.
   * @param reason     Human-readable reason for acknowledgement.
   */
  static async acknowledgeSoftBlock(
    userId: string,
    contactId: string,
    reason: string,
  ): Promise<void> {
    const { data: contactRow, error: fetchErr } = await supabase
      .from('estate_vault_contacts')
      .select('*')
      .eq('id', contactId)
      .eq('user_id', userId)
      .single();

    if (fetchErr || !contactRow) {
      logger.error('Failed to fetch contact for soft-block acknowledgement', {
        context: 'EstateContactDocumentWorkflowService',
        error: getErrorMessage(fetchErr),
      });
      throw new Error('WORKFLOW_CONTACT_NOT_FOUND');
    }

    const existing = readDocWorkflow(contactRow);
    const updatedWorkflow: Partial<DocWorkflowStatusSnapshot> = {
      ...existing,
      soft_block_acknowledged: true,
      soft_block_acknowledged_at: new Date().toISOString(),
      soft_block_acknowledged_reason: reason,
    };

    const updatedScope = mergeDocWorkflowIntoScope(contactRow, updatedWorkflow as DocWorkflowStatusSnapshot);

    const { error: updateErr } = await supabase
      .from('estate_vault_contacts')
      .update({ access_scope: updatedScope })
      .eq('id', contactId)
      .eq('user_id', userId);

    if (updateErr) {
      logger.error(
        'Failed to persist soft-block acknowledgement',
        {
          context: 'EstateContactDocumentWorkflowService',
          error: getErrorMessage(updateErr),
        },
      );
      throw new Error('WORKFLOW_SOFT_BLOCK_ACK_FAILED');
    }

    void EstateContactDocumentWorkflowService.emitAuditEvent(userId, {
      action: 'doc_workflow_soft_block_acknowledged',
      resource_type: 'estate_vault_contact',
      resource_id: contactId,
      details: { reason },
    });
  }

  /**
   * Emit a non-blocking audit event via the log_audit_event RPC.
   * Failures are logged but never propagate to callers.
   */
  private static async emitAuditEvent(
    userId: string,
    event: {
      action: string;
      resource_type: string;
      resource_id: string;
      details: Record<string, unknown>;
    },
  ): Promise<void> {
    try {
      await supabase.rpc('log_audit_event', {
        action: event.action,
        resource_type: event.resource_type,
        resource_id: event.resource_id,
        details: { ...event.details, actor_id: userId },
      });
    } catch (err) {
      logger.warn('Audit event emission failed (non-blocking)', {
        context: 'EstateContactDocumentWorkflowService',
        error: getErrorMessage(err),
      });
    }
  }
}

export type { DocWorkflowStatusSnapshot, DocumentRequirementItem, ContactRole, ContactDocRole };
